import {useTranslation} from '@/translations/i18n'
import {useFormContext} from 'react-hook-form'
import {NotesFormSchema} from '../../formSchemas'
import {z} from 'zod'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {NotesDescriptionField} from '../notes-description-field/NotesDescriptionField'
import {CardInEditModeType} from '@/pages/project-details-notes/ProjectDetailsNotes'
import {useStoreProjectNotes} from '../../queries/useStoreProjectNotes'
import {ProjectExtended, ProjectNotesType} from '../../types'
import {useOutletContext} from 'react-router-dom'
import {QUERY_KEYS, queryClient} from '@/queryClient'

export const OperationalNotesFormSchema = NotesFormSchema.pick({
    notesDescription: true
})
export type OperationalNotesFormSchema = z.infer<typeof OperationalNotesFormSchema>

interface OperationalNotesFormProps {
    setCardInEditMode: (cardInEditMode: CardInEditModeType) => void
}

export const OperationalNotesForm = ({setCardInEditMode}: OperationalNotesFormProps) => {
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {t} = useTranslation()
    const {
        handleSubmit,
        formState: {isDirty}
    } = useFormContext<OperationalNotesFormSchema>()

    const {mutate: createProjectNotes, isPending} = useStoreProjectNotes(
        {projectId: project.id},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_NOTES, project.id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
                setCardInEditMode(null)
            }
        }
    )

    const onSubmit = handleSubmit(data => {
        createProjectNotes({
            note: data.notesDescription ?? null,
            type: ProjectNotesType.enum.operational_notes
        })
    })

    const handleCancel = () => {
        setCardInEditMode(null)
    }

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:operational_notes')}
            headingEndSlot={
                <EditDetailsSectionButtons
                    isLoading={isPending}
                    isActive={true}
                    isDisabled={!isDirty}
                    onCancel={handleCancel}
                    onConfirm={onSubmit}
                />
            }
        >
            <NotesDescriptionField />
        </DetailsFieldsSection>
    )
}
